import type { RunState, ExtendedCertificate } from 'shared';
import type { FreeInputStep, MinigameStep } from '../engine/stepEngine';
import { PlayerApiClient, type FreeInputPayload, type FreeInputResponse, type MinigameResultPayload } from '../api/client';

export interface PersistenceAdapter {
  saveState(state: RunState): Promise<void>;
  recordMinigameResult(step: MinigameStep, result: Record<string, unknown>): Promise<void>;
  recordFreeInput(step: FreeInputStep, payload: Omit<FreeInputPayload, 'stepId'>): Promise<FreeInputResponse>;
  completeRun(endingId?: string): Promise<void>;
  generateCertificate(): Promise<ExtendedCertificate>;
}

export class MockPersistenceAdapter implements PersistenceAdapter {
  // P8-0: 生成時刻＝ラン開始とみなし、完了書の playTimeMs を実経過時間で算出する
  // （従来の固定 30 分は実プレイと無関係な捏造値だった）。
  private readonly startedAtMs = Date.now();

  // P8-0: 公開 Player（ticketless）の完了書に固定文字列「Mock Scenario」が露出していたため、
  // 実際に読み込まれたシナリオのタイトルを受け取る。
  constructor(private readonly scenarioTitle: string) {}

  async saveState(state: RunState): Promise<void> {
    console.debug('[mock] saveState', state);
  }

  async recordMinigameResult(step: MinigameStep, result: Record<string, unknown>): Promise<void> {
    console.debug('[mock] minigameResult', step.id, result);
  }

  async recordFreeInput(step: FreeInputStep, payload: Omit<FreeInputPayload, 'stepId'>): Promise<FreeInputResponse> {
    console.debug('[mock] freeInput', step.id, payload);
    const safeText = (payload.userText ?? '').trim();
    const summaryLabel = safeText ? safeText.slice(0, 24) : '（無言）';
    const routeKey = payload.suggestedRouteKey ?? step.defaultRouteKey ?? 'default';
    return {
      routeKey,
      summaryLabel,
      assistantLines: [
        safeText ? `なるほど。「${summaryLabel}」だね。` : '……了解。無言の意味も、ちゃんと受け取ったよ。',
        'じゃあ、このまま先へ進もう。'
      ]
    };
  }

  async completeRun(endingId?: string): Promise<void> {
    console.debug('[mock] completeRun', endingId);
  }

  async generateCertificate(): Promise<ExtendedCertificate> {
    console.debug('[mock] generateCertificate');
    // モックの証明書データを返す（詳細レポート付き）。
    // P8-0: 実プレイと無関係な断定（「聞き込みが丁寧」等の具体的行動評・架空アーティファクト）は
    // 捏造になるため排し、文言は「解決を記録した」という事実ベースの中立表現に絞る。
    return {
      certificateId: 'mock-cert-id',
      params: {
        analytical: 65,
        intuition: 72,
        persistence: 88,
        observation: 55,
        communication: 78,
        deduction: 70
      },
      analysisText: '依頼は解決として、九十九商会の台帳に記録されました。ここまでの歩みは、確かにお預かりしています。',
      scenarioTitle: this.scenarioTitle,
      endingType: 'true',
      playTimeMs: Date.now() - this.startedAtMs,
      completedAt: new Date().toISOString(),
      shareToken: 'mock-share-token',
      detailedReport: {
        detectiveType: 'IECT',
        detectiveAxis: {
          analytical: 45,
          logical: 40,
          cautious: 60,
          solo: 35
        },
        rarity: 'rare',
        behaviorAnalysis: {
          dialogStyle: {
            averageLength: 'medium',
            emotionalTone: 'balanced',
            responseRate: 78
          },
          minigameTendency: {
            preferredTypes: [],
            averageRetries: 0.8,
            completionRate: 85
          },
          choiceTendency: {
            riskTaking: 45,
            empathyFocus: 72,
            thoroughness: 75
          }
        },
        demonstratedStrengths: [
          '依頼を最後まで見届けました'
        ],
        areasForGrowth: [],
        notableActions: [],
        tsukumogamiEvaluation: {
          evaluatorName: '琴乃（九十九商会・事務担当）',
          overallGrade: 'A',
          comment: 'お疲れさまでした。事務担当の琴乃ですわ。今回の依頼は、解決として確かに台帳へ記録いたしました。またのご縁がありましたら、ぜひ九十九商会へ。',
          recommendation: '依頼を解決へ導いた協力者として、ここに記録します。',
          advice: '次の依頼でも、焦らず、目の前のものをよく見ることから。'
        },
        comparisonStats: null
      }
    };
  }
}

export class ApiPersistenceAdapter implements PersistenceAdapter {
  constructor(
    private readonly api: PlayerApiClient,
    private readonly runId: string,
    private readonly deviceKey: string
  ) {}

  saveState(state: RunState): Promise<void> {
    return this.api.saveRunState(this.runId, this.deviceKey, state);
  }

  recordMinigameResult(step: MinigameStep, result: Record<string, unknown>): Promise<void> {
    const payload: MinigameResultPayload = {
      stepId: step.id,
      registryId: step.registryId,
      result
    };
    return this.api.sendMinigameResult(this.runId, this.deviceKey, payload);
  }

  recordFreeInput(step: FreeInputStep, payload: Omit<FreeInputPayload, 'stepId'>): Promise<FreeInputResponse> {
    return this.api.sendFreeInput(this.runId, this.deviceKey, {
      stepId: step.id,
      ...payload
    });
  }

  completeRun(endingId?: string): Promise<void> {
    return this.api.completeRun(this.runId, this.deviceKey, endingId);
  }

  generateCertificate(): Promise<ExtendedCertificate> {
    return this.api.generateCertificate(this.runId, this.deviceKey);
  }
}
